import React, { useMemo } from 'react';
import { cn } from '../../../lib/utils';
import { useTheme } from '../../../contexts/ThemeContext';
import {
  useDisplayTimeZone,
  useDisplayTimeZoneCaption,
  useEffectiveDisplayTimeZone,
} from '../../../hooks/useDisplayTimeZone';
import { formatTimeZoneOffset } from '../../../utils/timeZone';
import { LastSyncHintPopover } from './LastSyncHintPopover';
import {
  buildLastSyncHintContent,
  formatLastSyncDisplay,
  type LastSyncAccountSnapshot,
} from '../utils/lastSyncHint';

interface LastSyncDateDisplayProps {
  lastSyncAt: string;
  accounts: LastSyncAccountSnapshot[];
  className?: string;
  /** `inline` = single row with time, offset, and info icon (app footer). */
  layout?: 'default' | 'inline';
}

export const LastSyncDateDisplay: React.FC<LastSyncDateDisplayProps> = ({
  lastSyncAt,
  accounts,
  className,
  layout = 'default',
}) => {
  const { colors } = useTheme();
  const displayTimeZone = useDisplayTimeZone();
  const effectiveZone = useEffectiveDisplayTimeZone();
  const zoneCaption = useDisplayTimeZoneCaption();
  const formatOpts = useMemo(
    () => (displayTimeZone ? { timeZone: displayTimeZone } : undefined),
    [displayTimeZone],
  );

  const formatted = useMemo(
    () => formatLastSyncDisplay(lastSyncAt, formatOpts),
    [lastSyncAt, formatOpts],
  );

  const offsetLabel = useMemo(
    () => formatTimeZoneOffset(effectiveZone, new Date(lastSyncAt)),
    [effectiveZone, lastSyncAt],
  );

  const hintContent = useMemo(
    () =>
      buildLastSyncHintContent({
        zoneCaption,
        effectiveZone,
        displayPreference: displayTimeZone,
        lastSyncAt,
        formatOpts,
        accounts,
      }),
    [zoneCaption, effectiveZone, displayTimeZone, lastSyncAt, formatOpts, accounts],
  );

  if (!formatted) {
    return <span className={cn(colors.textMuted, className)}>—</span>;
  }

  if (layout === 'inline') {
    return (
      <span className={cn('inline-flex items-center gap-1 min-w-0', className)}>
        <span className={cn('truncate tabular-nums font-semibold text-xs', colors.text)}>
          {formatted}
        </span>
        <span className={cn('opacity-70 shrink-0', colors.textMuted)} aria-hidden>
          ·
        </span>
        <span className={cn('shrink-0 tabular-nums text-xs font-medium', colors.textSecondary)}>
          {offsetLabel}
        </span>
        <LastSyncHintPopover content={hintContent} />
      </span>
    );
  }

  return (
    <div className={cn('flex items-center justify-between gap-2 min-w-0', className)}>
      <p className={cn('min-w-0 truncate tabular-nums', colors.text)}>
        <span className="font-semibold">{formatted}</span>
        <span className={cn('mx-1.5 opacity-70', colors.textMuted)} aria-hidden>
          ·
        </span>
        <span className={cn('text-xs font-medium', colors.textSecondary)}>{offsetLabel}</span>
      </p>
      <LastSyncHintPopover content={hintContent} />
    </div>
  );
};
